import httpClient from './httpClient';

export interface ReportingPeriodInfo {
  reporting_period_type: string;
  reporting_period_value: number;
  reporting_year: number;
  reporting_period_label: string;
  due_date: string;
}

export interface SubmissionStatus {
  state: string;
  updated_at?: string | null;
  updated_by?: string | null;
  notes?: string | null;
}

export interface SubawardeeReportRow {
  report_id: string;
  subawardee: string;
  initiative_lead: string | null;
  submission_status: SubmissionStatus;
  submitted_date: string | null;
  rejected_date: string | null;
  days_until_auto_approval: number | null;
}

export interface OutstandingReportBlock {
  component_project_id: string;
  reporting_period_type: string;
  reporting_period_value: number;
  reporting_year: number;
  reporting_period_label: string;
  subawardee_reports: SubawardeeReportRow[];
}

export interface OutstandingMilestone {
  id: string;
  report_id: string;
  title: string;
  description: string | null;
  status: string;
  due_date: string;
  subawardee: string;
  initiative_lead: string | null;
  activity_id?: string;
  goal_id?: string;
  key_activity_id?: string;
  objective_id?: string;
  component_project_id?: string;
}

export interface OutstandingBucket {
  component_project_id: string;
  reporting_period: ReportingPeriodInfo;
  reports: OutstandingReportBlock[];
  milestones: OutstandingMilestone[];
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface OutstandingItemsResponse {
  items: OutstandingBucket[];
  pagination: PaginationInfo;
  filters_applied: Record<string, unknown>;
}

export type OutstandingItemsSort =
  | 'dueDate-asc'
  | 'dueDate-desc'
  | 'component-asc'
  | 'component-desc';

export interface GetOutstandingItemsParams {
  page?: number;
  page_size?: number;
  sort_by?: OutstandingItemsSort;
  reporting_year?: number;
  reporting_period_value?: number;
  reporting_period_type?: string;
  subawardee?: string;
  /** Comma-separated; each segment must match a bucket `component_project_id` exactly (after server split/trim). */
  component_project?: string;
  /** Initiative lead slug (e.g. `tori-burris`). Server filters buckets to only that lead's subawardees before paginating. */
  initiative_lead?: string;
  /**
   * Comma-separated stored `submission_status.state` values (e.g. `pending_review,not_submitted`).
   * Omit = all non-approved outstanding reports (unchanged).
   */
  submission_status?: string;
  /**
   * Comma-separated milestone `status` values (e.g. `in_progress,not_started`).
   * Omit = all non-completed outstanding milestones (unchanged).
   */
  milestone_status?: string;
}

export interface AdminStatusOptionsResponse {
  report_submission_statuses: string[];
  milestone_statuses: string[];
}

export interface GetStatusOptionsParams {
  /** Comma-separated friendly names when using `x-admin-scope: all` (same as other admin list endpoints). */
  subawardee?: string;
}

/**
 * Outstanding reports/milestones buckets for the admin dashboard.
 * Uses x-admin-scope: all so staff see all mapped subawardees without x-selected-group.
 */
export async function getOutstandingItems(
  params: GetOutstandingItemsParams = {}
): Promise<OutstandingItemsResponse> {
  const response = await httpClient.get<OutstandingItemsResponse>('/v1/admin/outstanding-items', {
    params: {
      page: params.page ?? 1,
      page_size: params.page_size ?? 25,
      sort_by: params.sort_by ?? 'dueDate-asc',
      ...(params.reporting_year !== undefined && { reporting_year: params.reporting_year }),
      ...(params.reporting_period_value !== undefined && {
        reporting_period_value: params.reporting_period_value,
      }),
      ...(params.reporting_period_type && { reporting_period_type: params.reporting_period_type }),
      ...(params.subawardee && { subawardee: params.subawardee }),
      ...(params.component_project && { component_project: params.component_project }),
      ...(params.initiative_lead && { initiative_lead: params.initiative_lead }),
      ...(params.submission_status && { submission_status: params.submission_status }),
      ...(params.milestone_status && { milestone_status: params.milestone_status }),
    },
    headers: {
      'X-Admin-Scope': 'all',
    },
  });
  return response.data;
}

/**
 * Canonical + in-scope status tokens for admin outstanding-item filters.
 * Same auth as other admin routes (`api.admin` / `api.support`).
 */
export async function getStatusOptions(
  params: GetStatusOptionsParams = {}
): Promise<AdminStatusOptionsResponse> {
  const response = await httpClient.get<AdminStatusOptionsResponse>('/v1/admin/status-options', {
    params: {
      ...(params.subawardee && { subawardee: params.subawardee }),
    },
    headers: {
      'X-Admin-Scope': 'all',
    },
  });
  return response.data;
}

/**
 * Sorted list of initiative lead names for populating a select menu.
 */
export async function getInitiativeLeads(): Promise<string[]> {
  const response = await httpClient.get<{ leads: string[] }>('/v1/admin/initiative-leads', {
    headers: {
      'X-Admin-Scope': 'all',
    },
  });
  return response.data.leads;
}
